/**
 * /admin/override-requests — pending queue + history for rule-override
 * requests submitted by bookers (Phase 9.9). Tenant admins approve
 * (creates the booking under their identity, bypassing the 097/098
 * enforcement trigger) or deny with a note.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, ShieldX, RefreshCw, ClipboardList } from "lucide-react";
import {
  listOverrideRequests,
  approveOverrideRequest,
  denyOverrideRequest,
} from "@/lib/api";

const STATUS_BADGE = {
  pending:   { label: "Pending",   className: "bg-amber-100 text-amber-800 border-amber-300" },
  approved:  { label: "Approved",  className: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  denied:    { label: "Denied",    className: "bg-rose-100 text-rose-800 border-rose-300" },
  cancelled: { label: "Cancelled", className: "bg-slate-100 text-slate-700 border-slate-300" },
};

export default function AdminOverrideRequests() {
  const [statusFilter, setStatusFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);
  const [note, setNote] = useState({}); // {rowId: text}

  const refresh = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await listOverrideRequests({
        status: statusFilter === "all" ? null : statusFilter,
        limit: 100,
      });
      if (!cancelled) {
        setRows(data || []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [statusFilter, tick]);

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, denied: 0, cancelled: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  const onApprove = async (r) => {
    const { error } = await approveOverrideRequest(r.id, note[r.id] || null);
    if (error) return toast.error(error.message);
    toast.success("Booking created — override approved.");
    setNote((n) => ({ ...n, [r.id]: "" }));
    refresh();
  };

  const onDeny = async (r) => {
    const reason = (note[r.id] || "").trim();
    if (!reason) return toast.error("Please add a reason before denying.");
    const { error } = await denyOverrideRequest(r.id, reason);
    if (error) return toast.error(error.message);
    toast.success("Request denied — requester notified.");
    setNote((n) => ({ ...n, [r.id]: "" }));
    refresh();
  };

  return (
    <div className="space-y-4 p-6" data-testid="admin-override-requests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ClipboardList className="h-6 w-6" /> Override requests
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Bookers submit these when a site rule (operating hours, blackouts,
            minimum duration, slot alignment) blocks their booking. Approving
            creates the booking under your identity — the rule engine treats
            you as an authorised overrider.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} data-testid="override-refresh">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Queue</CardTitle>
            <div className="flex items-center gap-2">
              {(["pending", "approved", "denied", "cancelled", "all"]).map((s) => (
                <span key={s} className="text-[11px] text-slate-500">
                  {s}: <strong className="text-slate-800">{s === "all" ? rows.length : (counts[s] || 0)}</strong>
                </span>
              ))}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 w-32 text-xs" data-testid="override-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="denied">Denied</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && <p className="text-xs text-slate-500">Loading…</p>}
          {!loading && rows.length === 0 && (
            <p className="text-xs text-slate-500 italic">Nothing here — no requests match this filter.</p>
          )}
          {!loading && rows.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32">Status</TableHead>
                  <TableHead>Requester</TableHead>
                  <TableHead>Site / Bay</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead>Reason blocked</TableHead>
                  <TableHead className="w-80">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const s = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
                  return (
                    <TableRow key={r.id} data-testid={`override-row-${r.id}`}>
                      <TableCell>
                        <Badge variant="outline" className={`text-[10px] ${s.className}`}>{s.label}</Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="font-medium text-slate-800">
                          {r.requester?.first_name || r.requester?.last_name
                            ? `${r.requester.first_name || ""} ${r.requester.last_name || ""}`.trim()
                            : r.requester?.email || "—"}
                        </div>
                        <div className="text-slate-500">{r.requester?.email}</div>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="text-slate-800">{r.site?.name || "—"}</div>
                        <div className="text-slate-500">{r.bay?.name || "any bay"}</div>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div>{formatDt(r.scheduled_start)}</div>
                        <div className="text-slate-500">→ {formatDt(r.scheduled_end)}</div>
                      </TableCell>
                      <TableCell className="text-xs max-w-md">
                        <div className="text-rose-700">{r.rule_error}</div>
                        {r.action_note && (
                          <div className="text-slate-500 mt-1 italic">
                            Admin note: {r.action_note}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.status === "pending" ? (
                          <div className="space-y-1.5">
                            <Textarea
                              value={note[r.id] || ""}
                              onChange={(e) => setNote((n) => ({ ...n, [r.id]: e.target.value }))}
                              placeholder="Optional for approve · required for deny"
                              className="text-xs h-14"
                              data-testid={`override-note-${r.id}`}
                            />
                            <div className="flex gap-1.5">
                              <Button
                                size="sm"
                                variant="default"
                                className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => onApprove(r)}
                                data-testid={`override-approve-${r.id}`}
                              >
                                <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs text-rose-700 border-rose-300 hover:bg-rose-50"
                                onClick={() => onDeny(r)}
                                data-testid={`override-deny-${r.id}`}
                              >
                                <ShieldX className="h-3.5 w-3.5 mr-1" /> Deny
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs text-slate-500">
                            {r.actioner?.email ? `by ${r.actioner.email}` : "—"}
                            {r.actioned_at && <div>{formatDt(r.actioned_at)}</div>}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function formatDt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-AU", {
    weekday: "short", day: "2-digit", month: "short",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}
